import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Subscription, SubscriptionFilters, PaginatedResponse } from '@types'
import { subscriptionService } from '@services/subscriptionService'

interface SubscriptionState {
  subscriptions: Subscription[]
  loading: boolean
  error: string | null
  filters: SubscriptionFilters
  pagination: {
    page: number
    pageSize: number
    total: number
  }
  selectedSubscriptions: string[]
  statistics: {
    total: number
    active: number
    expired: number
    cancelled: number
    needsRenewal: number
    totalRevenue: number
  }
}

const initialState: SubscriptionState = {
  subscriptions: [],
  loading: false,
  error: null,
  filters: {
    status: 'all',
    gardenId: 'all',
    userId: 'all',
    startDate: '',
    endDate: '',
    search: '',
    page: 1,
    pageSize: 10,
  },
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
  },
  selectedSubscriptions: [],
  statistics: {
    total: 0,
    active: 0,
    expired: 0,
    cancelled: 0,
    needsRenewal: 0,
    totalRevenue: 0,
  },
}

// 异步actions
export const fetchSubscriptions = createAsyncThunk(
  'subscriptions/fetchSubscriptions',
  async (filters: SubscriptionFilters, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.getSubscriptions(filters)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || '获取订阅列表失败')
    }
  }
)

export const updateSubscriptionStatus = createAsyncThunk(
  'subscriptions/updateStatus',
  async ({ id, status }: { id: string; status: 'active' | 'expired' | 'cancelled' }, { rejectWithValue }) => {
    try {
      await subscriptionService.updateSubscriptionStatus(id, status)
      return { id, status }
    } catch (error: any) {
      return rejectWithValue(error.message || '更新订阅状态失败')
    }
  }
)

export const batchUpdateStatus = createAsyncThunk(
  'subscriptions/batchUpdateStatus',
  async ({ ids, status }: { ids: string[]; status: 'active' | 'expired' | 'cancelled' }, { rejectWithValue }) => {
    try {
      await subscriptionService.batchUpdateStatus(ids, status)
      return { ids, status }
    } catch (error: any) {
      return rejectWithValue(error.message || '批量更新订阅状态失败')
    }
  }
)

export const fetchStatistics = createAsyncThunk(
  'subscriptions/fetchStatistics',
  async (filters: SubscriptionFilters, { rejectWithValue }) => {
    try {
      const statistics = await subscriptionService.getSubscriptionStatistics(filters)
      return statistics
    } catch (error: any) {
      return rejectWithValue(error.message || '获取订阅统计失败')
    }
  }
)

const subscriptionSlice = createSlice({
  name: 'subscriptions',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<SubscriptionFilters>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    setPagination: (state, action: PayloadAction<Partial<{ page: number; pageSize: number; total: number }>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    setSelectedSubscriptions: (state, action: PayloadAction<string[]>) => {
      state.selectedSubscriptions = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    clearSelected: (state) => {
      state.selectedSubscriptions = []
    },
  },
  extraReducers: (builder) => {
    builder
      // 获取订阅列表
      .addCase(fetchSubscriptions.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSubscriptions.fulfilled, (state, action: PayloadAction<PaginatedResponse<Subscription>>) => {
        state.loading = false
        state.subscriptions = action.payload.data
        state.pagination = {
          page: action.payload.page,
          pageSize: action.payload.pageSize,
          total: action.payload.total,
        }
        state.error = null
      })
      .addCase(fetchSubscriptions.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      
      // 更新订阅状态
      .addCase(updateSubscriptionStatus.fulfilled, (state, action) => {
        const { id, status } = action.payload
        const subscription = state.subscriptions.find(sub => sub.id === id)
        if (subscription) {
          subscription.status = status
        }
      })
      
      // 批量更新状态
      .addCase(batchUpdateStatus.fulfilled, (state, action) => {
        const { ids, status } = action.payload
        state.subscriptions.forEach(subscription => {
          if (ids.includes(subscription.id)) {
            subscription.status = status
          }
        })
        state.selectedSubscriptions = []
      })
      
      // 获取统计
      .addCase(fetchStatistics.fulfilled, (state, action) => {
        state.statistics = action.payload
      })
  },
})

export const { setFilters, setPagination, setSelectedSubscriptions, clearError, clearSelected } = subscriptionSlice.actions
export default subscriptionSlice.reducer
